"use client";
// Widget de reserva: horario → personas → datos → apartado (15 min) → pago → QR.
// Los cupos y el total los decide el servidor. Solo la exportación estática permite simulación.
import { Icono } from "@/components/Iconos";
import { CamionLleno } from "@/components/CamionLleno";
import { irAPagarEnLinea } from "@/lib/pago-en-linea-cliente";
import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import type { Idioma } from "@/content/catalogo";
import { calcularTotal, formatoMXN } from "@/lib/dinero";
import { diccionario } from "@/lib/i18n";
import { Confeti } from "./Deco";
import { lugaresDelGrupo } from "@/lib/grupo-boletos";
import { urlBoleto } from "@/lib/boleto";
import { operacion } from "@/panel/operacion";
import { agregarAlItinerario, guardarDatosVisita, leerDatosVisita } from "@/lib/itinerario";
import { CalendarioVisita } from "@/components/CalendarioVisita";
import { Recomendaciones, RutaExplorador } from "@/components/Recomendaciones";
import { ajustarEdades, EdadesMenores, edadesListas, useReglasMenores, type Edad } from "@/components/EdadesMenores";
import { cotizarMenores } from "@/lib/menores";
import { agregarAlCarrito, leerCarrito, MAX_LINEAS_CARRITO } from "@/lib/carrito";
import { Img } from "@/components/Img";
import { PistaPromo } from "@/components/PistaPromo";
import type { Pista } from "@/lib/promociones";

type Salida = { salidaId: string; hora: string; capacidad: number; libres: number; camiones: number };
type Persona = { codigo: string; etiqueta: string };
type Venta = { folio: string; totalCentavos: number; apartadaHasta: string | null; codigoQr: string; personas?: Persona[] };

type Props = {
  idioma: Idioma;
  slug: string;
  adultoCentavos: number;
  ninoCentavos: number;
  precioNinoConfirmado: boolean;
  porVuelo: boolean;
  horariosPrueba: string[];
  nombreTour: string;
  duracionMin: number;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const demoEstatica = process.env.NEXT_PUBLIC_EXPORT === "1";

// Disponibilidad simulada y estable por fecha/hora, solo para la vista previa sin servidor.
function simular(slug: string, fecha: string, horas: string[], porVuelo: boolean): Salida[] {
  return horas.map((hora, i) => {
    const semilla = [...`${slug}${fecha}${hora}`].reduce((n, c) => (n * 31 + c.charCodeAt(0)) % 9973, 7);
    const capacidad = porVuelo ? 5 : slug === "sierra-del-tigre" && hora === "12:00" ? 40 : 20;
    const libres = porVuelo ? (semilla % 2 ? 5 : 0) : Math.max(0, capacidad - (semilla % (capacidad + 3)));
    return { salidaId: `demo-${fecha}-${i}`, hora, capacidad, libres, camiones: capacidad > 20 ? 2 : 1 };
  });
}

export function Reserva(p: Props) {
  const t = diccionario(p.idioma).reserva;
  const [fecha, setFecha] = useState(hoyISO());
  const [salidas, setSalidas] = useState<Salida[] | null>(null);
  const [demo, setDemo] = useState(false);
  const [elegida, setElegida] = useState<string | null>(null);
  const [adultos, setAdultos] = useState(2);
  const [ninos, setNinos] = useState(0);
  const [edades, setEdades] = useState<Edad[]>([]);
  useEffect(() => { setEdades((e) => ajustarEdades(e, ninos)); }, [ninos]);
  const [paso, setPaso] = useState<"elegir" | "datos" | "apartado" | "listo">("elegir");
  const [cliente, setCliente] = useState({ nombre: "", telefono: "", correo: "" });
  const [venta, setVenta] = useState<Venta | null>(null);
  const [error, setError] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [qr, setQr] = useState("");
  const [ahora, setAhora] = useState(Date.now());
  const [datosGuardados, setDatosGuardados] = useState(false);
  const [otraPersona, setOtraPersona] = useState(false);
  const [horaEnlace, setHoraEnlace] = useState("");
  const [deEnlace, setDeEnlace] = useState(false);
  const [sinPasarela, setSinPasarela] = useState(false);
  // Al cambiar de paso, la pantalla vuelve al inicio del widget: el siguiente paso no queda escondido más abajo.
  const raiz = useRef<HTMLDivElement>(null), primerPaso = useRef(true);
  useEffect(() => {
    if (primerPaso.current) { primerPaso.current = false; return; }
    const el = raiz.current; if (!el) return;
    const quieto = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Al apartar, lo que importa es el botón de pago: se centra en la pantalla.
    const pago = paso === "apartado" ? el.querySelector<HTMLElement>(".reserva-pagar") : null;
    if (pago) pago.scrollIntoView({ block: "center", behavior: quieto ? "auto" : "smooth" });
    else if (el.getBoundingClientRect().top < 0 || el.getBoundingClientRect().top > window.innerHeight * 0.4) el.scrollIntoView({ block: "start", behavior: quieto ? "auto" : "smooth" });
    el.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
  }, [paso]);
  // Mientras la reserva está a la vista, la barra «Reservar» del teléfono se esconde: no tapa los botones del paso.
  useEffect(() => {
    const el = raiz.current; if (!el || typeof IntersectionObserver === "undefined") return;
    const o = new IntersectionObserver(([e]) => document.body.classList.toggle("reserva-a-la-vista", e.isIntersecting), { threshold: 0.05 });
    o.observe(el);
    return () => { o.disconnect(); document.body.classList.remove("reserva-a-la-vista"); };
  }, [paso]);
  const disponibilidadDia = useCallback(async (dia: string): Promise<boolean | null> => {
    try {
      const respuesta = await fetch(`/api/disponibilidad?tour=${p.slug}&fecha=${dia}`);
      if (!respuesta.ok) throw new Error();
      const datos = await respuesta.json() as { salidas: Salida[] };
      return datos.salidas.some((salida) => salida.libres > 0);
    } catch { return demoEstatica ? simular(p.slug, dia, p.horariosPrueba, p.porVuelo).some((salida) => salida.libres > 0) : null; }
  }, [p.horariosPrueba, p.porVuelo, p.slug]);

  // Lee fecha y personas que vienen del buscador de la portada.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const f = q.get("fecha");
    const a = Number(q.get("adultos"));
    const n = Number(q.get("ninos"));
    const h = q.get("hora");
    if (f && /^\d{4}-\d{2}-\d{2}$/.test(f) && f >= hoyISO()) setFecha(f);
    if (a >= 1 && a <= 12) { setAdultos(p.porVuelo ? Math.min(a, 5) : a); setDeEnlace(true); }
    if (!p.porVuelo && n >= 1 && n <= 11) setNinos(n);
    if (h && /^\d{2}:\d{2}$/.test(h)) setHoraEnlace(h);
    const datos = leerDatosVisita();
    if (datos) { setCliente(datos); setDatosGuardados(true); }
  }, [p.porVuelo]);

  // Día sin salidas: se busca el siguiente que sí tenga (hasta dos semanas) para ofrecerlo con un toque.
  const [siguiente, setSiguiente] = useState<string | null>(null);
  useEffect(() => {
    setSiguiente(null);
    if (!salidas || salidas.some((s) => s.libres > 0)) return;
    let vivo = true;
    void (async () => {
      for (let i = 1; i <= 14 && vivo; i++) {
        const d = new Date(`${fecha}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + i);
        const dia = d.toISOString().slice(0, 10);
        if (await disponibilidadDia(dia)) { if (vivo) setSiguiente(dia); return; }
      }
    })();
    return () => { vivo = false; };
  }, [salidas, fecha, disponibilidadDia]);

  // Sin hora en el enlace, se elige sola la primera salida con lugar: así el camión y Cleto se ven desde el principio.
  useEffect(() => {
    if (horaEnlace || !salidas || elegida) return;
    const primera = salidas.find((s) => s.libres >= adultos + ninos) ?? salidas.find((s) => s.libres > 0);
    if (primera) setElegida(primera.salidaId);
  }, [salidas, horaEnlace, elegida]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!horaEnlace || !salidas || elegida) return;
    const coincidencia = salidas.find((s) => s.hora === horaEnlace && s.libres >= adultos + ninos);
    if (coincidencia) setElegida(coincidencia.salidaId);
  }, [adultos, elegida, horaEnlace, ninos, salidas]);

  useEffect(() => {
    let vivo = true;
    setSalidas(null);
    setElegida(null);
    setError("");
    setDemo(false);
    fetch(`/api/disponibilidad?tour=${p.slug}&fecha=${fecha}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d: { salidas: Salida[] }) => vivo && setSalidas(d.salidas))
      .catch(async () => {
        if (!vivo) return;
        if (!demoEstatica) {
          setSalidas([]);
          setError(p.idioma === "es" ? "No pudimos consultar la disponibilidad. Intenta de nuevo más tarde." : "Availability could not be checked. Please try again later.");
          return;
        }
        setDemo(true);
        // Vista previa sin servidor: los mismos datos de prueba que ve el panel (si no cargan, se simula).
        try {
          const op = await operacion();
          const t = (await op.catalogo()).find((x) => x.slug === p.slug);
          if (!t) throw new Error();
          const l = await op.salidas(t.id, fecha, "web");
          if (vivo) setSalidas(l);
        } catch { if (vivo) setSalidas(simular(p.slug, fecha, p.horariosPrueba, p.porVuelo)); }
      });
    return () => { vivo = false; };
  }, [fecha, p.slug, p.horariosPrueba, p.porVuelo, p.idioma]);

  useEffect(() => {
    if (paso !== "apartado") return;
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [paso]);

  useEffect(() => {
    if (venta?.codigoQr && paso === "listo") QRCode.toDataURL(venta.codigoQr, { margin: 1, width: 400 }).then(setQr);
  }, [venta, paso]);

  const salida = salidas?.find((s) => s.salidaId === elegida) ?? null;
  const personas = adultos + ninos;
  // Reglas de menores del tour: la edad de cada uno define su tarifa y si ocupa lugar (los de cortesía en piernas no).
  const reglas = useReglasMenores(p.slug, !demo && !p.porVuelo);
  const listas = edadesListas(edades, ninos);
  const cotMen = reglas && ninos > 0 && listas ? cotizarMenores(edades as number[], reglas.tramos, p.adultoCentavos) : null;
  const ocupan = adultos + (cotMen ? cotMen.ocupan : ninos);
  const total = p.porVuelo ? p.adultoCentavos : cotMen ? adultos * p.adultoCentavos + cotMen.brutoCentavos : calcularTotal({ adultos, ninos, adultoCentavos: p.adultoCentavos, ninoCentavos: p.ninoCentavos });
  // Promociones: el servidor cotiza con las reglas vigentes y el cliente ve cuánto ahorra y qué le falta para ahorrar más.
  const [cot, setCot] = useState<{ subtotalCentavos: number; descuentoCentavos: number; totalCentavos: number; aplicadas: { id: string; nombre: string; ahorroCentavos: number }[]; sugerencias: string[]; pistas?: Pista[]; nivel: { nombre: string; visitas: number } | null } | null>(null);
  const [codigo, setCodigo] = useState(""), [verCodigo, setVerCodigo] = useState(false);
  const [pista, setPista] = useState<Pista | null>(null);
  const telValido = /^\d{10}$/.test(cliente.telefono) ? cliente.telefono : undefined;
  useEffect(() => {
    if (demoEstatica || demo || !salida || !listas) { setCot(null); return; }
    let vivo = true;
    const espera = setTimeout(() => {
      fetch("/api/promociones/cotizar", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ lineas: [{ salidaId: salida.salidaId, adultos, ninos, ...(ninos > 0 && !p.porVuelo ? { edades: edades as number[] } : {}) }], codigo: codigo.trim() || undefined, telefono: telValido }) })
        .then((r) => (r.ok ? r.json() : null)).then((d) => { if (vivo) setCot(d); }).catch(() => { if (vivo) setCot(null); });
    }, 350);
    return () => { vivo = false; clearTimeout(espera); };
  }, [salida, adultos, ninos, edades, listas, codigo, telValido, demo]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalFinal = cot ? cot.totalCentavos : total;
  const maximo = p.porVuelo ? 5 : Math.min(12, salida ? salida.libres : 12);
  const restantes = salida ? salida.libres - ocupan : null;
  const prisa = restantes !== null && restantes <= 6;

  const segundos = venta?.apartadaHasta ? Math.max(0, Math.floor((new Date(venta.apartadaHasta).getTime() - ahora) / 1000)) : 0;

  async function apartar() {
    setError("");
    if (!salida) return;
    if (!listas) { setError(p.idioma === "es" ? "Indica la edad de cada menor." : "Please enter each child's age."); return; }
    guardarDatosVisita(cliente);
    setOcupado(true);
    try {
      if (demo && salida.salidaId.startsWith("demo-")) {
        await new Promise((r) => setTimeout(r, 700));
        setVenta({ folio: `MST-DEMO${Math.floor(Math.random() * 900 + 100)}`, totalCentavos: total, apartadaHasta: new Date(Date.now() + 15 * 60_000).toISOString(), codigoQr: `MST1.DEMO.${Date.now()}.1`, personas: lugaresDelGrupo(adultos, ninos).map((l, i) => ({ codigo: `MST1.DEMO.${Date.now()}.${i + 1}`, etiqueta: l.etiqueta })) });
      } else if (demo) {
        const op = await operacion();
        const v = await op.apartar({ salidaId: salida.salidaId, adultos, ninos, nombre: cliente.nombre, telefono: cliente.telefono, canal: "web", metodo: "liga" });
        setVenta({ folio: v.folio, totalCentavos: v.total, apartadaHasta: new Date(Date.now() + 15 * 60_000).toISOString(), codigoQr: v.codigo, personas: v.personas });
      } else {
        const r = await fetch("/api/reservas", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ salidaId: salida.salidaId, adultos, ninos, ...(ninos > 0 && !p.porVuelo ? { edades: edades as number[] } : {}), cliente: { ...cliente, idioma: p.idioma }, codigo: codigo.trim() || undefined }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.mensaje ?? t.error);
        setVenta(d);
        // Con pasarela activa se va directo a pagar: sin una pantalla intermedia con otro botón de pago.
        if (await irAPagarEnLinea(d.folio)) return;
        setSinPasarela(true);
      }
      setPaso("apartado");
    } catch (e) {
      setError(e instanceof Error ? e.message : t.error);
    } finally {
      setOcupado(false);
    }
  }

  async function pagar() {
    if (!venta) return;
    setOcupado(true);
    setError("");
    try {
      if (!demo) {
        if (!sinPasarela && await irAPagarEnLinea(venta.folio)) return; // con Mercado Pago activo, el cliente paga allá y vuelve a la página de resultado
        const r = await fetch(`/api/reservas/${venta.folio}/pago-prueba`, { method: "POST" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.mensaje ?? t.error);
        setVenta(d);
      } else if (!venta.folio.startsWith("MST-DEMO")) { await (await operacion()).pagar(venta.folio); }
      else await new Promise((r) => setTimeout(r, 900));
      setPaso("listo");
    } catch (e) {
      setError(e instanceof Error ? e.message : t.error);
    } finally {
      setOcupado(false);
    }
  }

  const [enCarrito, setEnCarrito] = useState("");
  // «Añadir a mi visita»: el tour queda en el plan del día (con cabaña, comida y bar) y en el pago conjunto de tours.
  function anadirAVisita(): boolean {
    if (!salida) return false;
    if (demoEstatica || demo) { sumarAlItinerario(); setEnCarrito(p.idioma === "es" ? "Quedó en tu visita." : "Added to your visit."); return true; }
    const ya = leerCarrito().some((x) => x.salidaId === salida.salidaId);
    const ok = agregarAlCarrito({ salidaId: salida.salidaId, tour: p.nombreTour, slug: p.slug, fecha, hora: salida.hora, adultos, ninos: p.porVuelo ? 0 : ninos, ...(!p.porVuelo && ninos > 0 && cotMen ? { edades: edades as number[], menoresCentavos: cotMen.brutoCentavos } : {}), adultoCentavos: p.adultoCentavos, ninoCentavos: p.ninoCentavos, porVuelo: p.porVuelo });
    if (ok) sumarAlItinerario();
    setEnCarrito(ok ? (ya ? (p.idioma === "es" ? "Actualizamos este tour en tu visita." : "Updated this tour in your visit.") : (p.idioma === "es" ? "Quedó en tu visita." : "Added to your visit.")) : (p.idioma === "es" ? `Tu visita admite hasta ${MAX_LINEAS_CARRITO} tours por pagar juntos.` : `Your visit holds up to ${MAX_LINEAS_CARRITO} tours to pay together.`));
    return ok;
  }

  function sumarAlItinerario() {
    if (!salida) return;
    const item = { id: `tour-${p.slug}-${fecha}-${salida.hora}`, lugar: p.nombreTour, tipo: "tour" as const, fecha, hora: salida.hora, personas, duracion: p.duracionMin, detalle: p.idioma === "es" ? "Tour" : "Tour" };
    agregarAlItinerario({ ...item, continuarEn: `/${p.idioma}/tours/${p.slug}?fecha=${fecha}&adultos=${adultos}&hora=${encodeURIComponent(salida.hora)}#reservar` });
  }

  if (paso === "listo" && venta) {
    return (
      <div ref={raiz} className="reserva exito" aria-live="polite" style={{ position: "relative", overflow: "hidden" }}>
        <Confeti />
        <video src="/media/s_festeja.mp4" poster="/media/cleto_festeja.webp" autoPlay muted loop playsInline aria-hidden="true" />
        <h2 tabIndex={-1} style={{ fontSize: "1.6rem" }}>{t.listo}</h2>
        <p>{t.folio}: <strong>{venta.folio}</strong></p>
        {qr && <Img className="qr" src={qr} alt={`QR ${venta.folio}`} />}
        <p className="tenue">{t.qr}</p>
        <p className="tenue">{fecha} · {salida?.hora} · {t.desglose(adultos, ninos)}</p>
        {venta.personas && venta.personas.length > 1 && (
          <div className="grupo-boletos">
            <p className="tenue">{p.idioma === "es" ? "Cada persona sube con su propio QR. Manda a cada quien el suyo:" : "Everyone boards with their own QR. Send each person theirs:"}</p>
            <ul>
              {venta.personas.map((x) => (
                <li key={x.codigo}>
                  <span>{x.etiqueta}</span>
                  <a href={urlBoleto(x.codigo, p.idioma, { folio: venta.folio, codigo: x.codigo, etiqueta: x.etiqueta, tour: p.nombreTour, slug: p.slug, fecha, hora: salida?.hora ?? "", adultos: x.etiqueta.startsWith("Menor") ? 0 : 1, ninos: x.etiqueta.startsWith("Menor") ? 1 : 0, total: venta.totalCentavos, nombre: cliente.nombre.split(" ")[0], estado: "vigente" })}>{p.idioma === "es" ? "Ver boleto" : "View ticket"}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
        <a className="boton verde" href={urlBoleto(venta.codigoQr, p.idioma, { folio: venta.folio, codigo: venta.codigoQr, etiqueta: venta.personas?.[0]?.etiqueta, tour: p.nombreTour, slug: p.slug, fecha, hora: salida?.hora ?? "", adultos: venta.personas?.length ? 1 : adultos, ninos: venta.personas?.length ? 0 : ninos, total: venta.totalCentavos, nombre: cliente.nombre.split(" ")[0], estado: "vigente" })}>
          {venta.personas && venta.personas.length > 1 ? (p.idioma === "es" ? `Ver mi boleto (${venta.personas[0].etiqueta})` : `View my ticket (${venta.personas[0].etiqueta})`) : (p.idioma === "es" ? "Ver mi boleto" : "View my ticket")}
        </a>
      </div>
    );
  }

  return (
    <div ref={raiz} className="reserva" aria-live="polite">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <video key={paso} src={`/media/s_${paso === "elegir" ? "hola" : paso === "datos" ? "piensa" : "prisa"}.mp4`} poster={`/media/cleto_${paso === "elegir" ? "hola" : paso === "datos" ? "piensa" : "prisa"}.webp`} autoPlay muted loop playsInline aria-hidden="true" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", background: "var(--sol)", border: "3px solid var(--sol)", animation: "flota 4s ease-in-out infinite" }} />
        <div>
          <h2 tabIndex={-1} style={{ fontSize: "1.5rem" }}>{t.titulo}</h2>
          <small className="tenue">{paso === "elegir" ? (p.idioma === "es" ? "Elige tu horario y súbete" : "Pick a time and hop on") : paso === "datos" ? (p.idioma === "es" ? "¿A quién le mando tu QR?" : "Where do I send your QR?") : (p.idioma === "es" ? "¡Apúrate, te guardo el lugar!" : "Hurry, I'm holding your seat!")}</small>
        </div>
      </div>

      {paso === "elegir" && (
        <>
          <div className="campo"><span>{t.fecha}</span><CalendarioVisita idioma={p.idioma} fecha={fecha} cambiarFecha={setFecha} disponible={disponibilidadDia} hoy={hoyISO()} /></div>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 700, fontSize: "0.92rem", marginBottom: 6 }}>{t.hora}</legend>
            {!salidas && <p className="tenue">…</p>}
            {error && <p className="error" role="alert">{error}</p>}
            {salidas && salidas.length === 0 && !error && <p className="tenue">{t.sinHorarios}</p>}
            {siguiente && !error && <button type="button" className="boton fantasma chico siguiente-dia" onClick={() => setFecha(siguiente)}>
              {p.idioma === "es" ? "Ver el siguiente día con salidas: " : "Next day with departures: "}{new Date(`${siguiente}T12:00:00Z`).toLocaleDateString(p.idioma === "es" ? "es-MX" : "en-US", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })} →
            </button>}
            <div className="horas">
              {salidas?.map((s) => (
                <button
                  key={s.salidaId}
                  type="button"
                  aria-pressed={elegida === s.salidaId}
                  disabled={s.libres === 0}
                  className={s.libres > 0 && s.libres <= 6 ? "poco" : ""}
                  onClick={() => { setElegida(s.salidaId); if (!p.porVuelo && ocupan > s.libres) { setAdultos(Math.max(1, Math.min(adultos, s.libres))); setNinos(0); } }}
                >
                  {s.hora}
                  <small>{s.libres === 0 ? t.lleno : t.libres(s.libres)} {t.camiones(s.camiones)}</small>
                </button>
              ))}
            </div>
          </fieldset>

          <Contador etiqueta={t.adultos} valor={adultos} min={1} max={maximo - ninos} cambiar={setAdultos} />
          {!p.porVuelo && <Contador etiqueta={t.ninos} valor={ninos} min={0} max={maximo - adultos} cambiar={setNinos} />}
          {deEnlace && !p.porVuelo && ninos === 0 && <p className="reserva-nota">{p.idioma === "es" ? "Contamos a todos como adultos. Si alguien es menor de edad, pásalo a «Menores»: su tarifa puede ser distinta." : "Everyone is counted as an adult. If someone is a minor, move them to «Children»: the rate may differ."}</p>}
          {!p.porVuelo && <EdadesMenores idioma={p.idioma} edades={edades} cambiar={setEdades} reglas={reglas} />}

          {salida && (
            <div className={`llenado${prisa ? " prisa" : ""}`}>
              {p.porVuelo ? (
                <video src="/media/ts_side.mp4" poster="/media/camion_poster.webp" autoPlay muted loop playsInline aria-hidden="true" />
              ) : (
                <CamionLleno capacidad={salida.capacidad} vendidos={salida.capacidad - salida.libres} adultos={adultos} menores={cotMen ? cotMen.ocupan : ninos} camiones={salida.camiones} />
              )}
              <div className="llenado-texto">
                <video key={prisa ? "prisa" : "hola"} className="llenado-cleto" src={prisa ? "/media/s_prisa.mp4" : "/media/s_hola.mp4"} poster={prisa ? "/media/cleto_prisa.webp" : "/media/cleto_hola.webp"} autoPlay muted loop playsInline aria-hidden="true" />
                {prisa ? <strong>{t.prisa(Math.max(0, restantes ?? 0))}</strong> : <span>{t.libres(salida.libres)}</span>}
                {!p.porVuelo && <small className="tenue">{t.desglose(adultos, ninos)}</small>}
              </div>
            </div>
          )}

          <div className="total">
            <span>
              {t.total}
              <br />
              <small className="tenue">{p.porVuelo ? (p.idioma === "es" ? "por vuelo" : "per flight") : t.desglose(adultos, ninos)}</small>
              {!p.precioNinoConfirmado && ninos > 0 && <><br /><small className="tenue">{p.idioma === "es" ? "Tarifa de menor por confirmar" : "Child rate to be confirmed"}</small></>}
            </span>
            <strong>{formatoMXN(totalFinal, p.idioma)}</strong>
          </div>
          {cot && cot.descuentoCentavos > 0 && <div className="promo-aviso" role="status">
            <p className="promo-aviso-t">🎉 {p.idioma === "es" ? "Ahorras" : "You save"} <b>{formatoMXN(cot.descuentoCentavos, p.idioma)}</b> <s className="tenue">{formatoMXN(cot.subtotalCentavos, p.idioma)}</s></p>
            <ul>{cot.aplicadas.map((a) => <li key={a.id}><span>✔ {a.nombre}</span><span>− {formatoMXN(a.ahorroCentavos, p.idioma)}</span></li>)}</ul>
          </div>}
          {cot && (cot.pistas ?? cot.sugerencias.map((texto) => ({ texto }) as Pista)).map((x) => x.tipo
            ? <button key={x.texto} type="button" className="promo-sugiere es-boton" onClick={() => setPista(x)}><span>💡 {x.texto}</span><b>{p.idioma === "es" ? "Ver cómo" : "Show me"}</b></button>
            : <p key={x.texto} className="promo-sugiere">💡 {x.texto}</p>)}
          {pista && <PistaPromo pista={pista} idioma={p.idioma} slugActual={p.slug} enVisita={leerCarrito().map((x) => x.slug)} fecha={fecha} adultos={adultos} maxPersonas={Math.max(0, maximo - adultos - ninos)} cerrar={() => setPista(null)} sumarPersonas={(n) => setAdultos((a) => Math.min(a + n, maximo - ninos))} guardarEsteTour={anadirAVisita} />}
          {cot?.nivel && <p className="promo-nivel-p">{({ Aventurero: "aventurero", "Guía de la sierra": "guia", Leyenda: "leyenda" } as Record<string, string>)[cot.nivel.nombre] ? <Img src={`/img/insignias/${({ Aventurero: "aventurero", "Guía de la sierra": "guia", Leyenda: "leyenda" } as Record<string, string>)[cot.nivel.nombre]}.webp`} alt="" width={28} height={28} /> : "🏆 "}{p.idioma === "es" ? "Tu nivel" : "Your level"}: <b>{cot.nivel.nombre}</b></p>}
          {!demoEstatica && !demo && <div className="promo-codigo">{verCodigo
            ? <label className="campo">{p.idioma === "es" ? "Código promocional" : "Promo code"}<input type="text" value={codigo} maxLength={24} autoCapitalize="characters" onChange={(e) => setCodigo(e.target.value.toUpperCase())} /></label>
            : <button type="button" className="enlace-codigo" onClick={() => setVerCodigo(true)}>{p.idioma === "es" ? "¿Tienes un código promocional?" : "Have a promo code?"}</button>}</div>}
          {!listas && <p className="tenue" role="status">{p.idioma === "es" ? "Indica la edad de cada menor para ver el precio." : "Enter each child's age to see the price."}</p>}
          <button className="boton reserva-principal" type="button" disabled={!salida || !listas} onClick={() => setPaso("datos")}>{p.idioma === "es" ? "Reservar este tour" : "Book this tour"}</button>
          <p className="reserva-cuando">{p.idioma === "es" ? "Apartas tus lugares 15 minutos, pagas en línea y te llega tu QR." : "We hold your seats for 15 minutes, you pay online and get your QR."}</p>
          <div className="reserva-otro">
            <div>
              <strong>{p.idioma === "es" ? "¿Vas a hacer más cosas?" : "Planning more?"}</strong>
              <small>{p.idioma === "es" ? "Júntalo con otros tours, tu cabaña y dónde comer. Todos los tours se pagan en un solo pago." : "Combine it with other tours, your cabin and where to eat. All tours are paid in one payment."}</small>
            </div>
            <button className="boton fantasma" type="button" disabled={!salida || !listas} onClick={() => anadirAVisita()}>{p.idioma === "es" ? "Añadir a mi visita" : "Add to my visit"}</button>
          </div>
          {enCarrito && <><p role="status" className="reserva-agregado">{enCarrito} <a href={`/${p.idioma}/itinerario`}>{p.idioma === "es" ? "Ver mi visita" : "View my visit"}</a>{!demoEstatica && !demo && <> · <a href={`/${p.idioma}/carrito`}>{p.idioma === "es" ? "Pagar tours juntos" : "Pay tours together"}</a></>}</p>
          {!demoEstatica && !demo && <><RutaExplorador idioma={p.idioma} tours={new Set(leerCarrito().map((x) => x.slug)).size} />
          <Recomendaciones idioma={p.idioma} slugs={leerCarrito().map((x) => x.slug)} fecha={fecha} personas={adultos} /></>}</>}
        </>
      )}

      {paso === "datos" && (
        <form className="pila" style={{ display: "grid", gap: 12 }} onSubmit={(e) => { e.preventDefault(); apartar(); }}>
          <Resumen idioma={p.idioma} tour={p.nombreTour} fecha={fecha} hora={salida?.hora ?? ""} personas={p.porVuelo ? (p.idioma === "es" ? `${adultos} en el vuelo` : `${adultos} on the flight`) : t.desglose(adultos, ninos)} total={formatoMXN(totalFinal, p.idioma)} editar={() => setPaso("elegir")} />
          {datosGuardados && <label className="campo"><span><input type="checkbox" checked={otraPersona} onChange={(e) => { const usarOtra = e.target.checked; setOtraPersona(usarOtra); if (usarOtra) setCliente({ nombre: "", telefono: "", correo: "" }); else { const datos = leerDatosVisita(); if (datos) setCliente(datos); } }} /> {p.idioma === "es" ? "Reservar para otra persona" : "Book for someone else"}</span><small className="tenue">{otraPersona ? (p.idioma === "es" ? "Escribe los datos de la otra persona." : "Enter the other person's details.") : (p.idioma === "es" ? "Usamos tu nombre y WhatsApp de la vez pasada." : "We will use your visit details.")}</small></label>}
          <label className="campo">{t.nombre}
            <input type="text" required minLength={3} autoComplete="name" value={cliente.nombre} onChange={(e) => setCliente({ ...cliente, nombre: e.target.value })} />
          </label>
          <label className="campo">{t.telefono}
            <input type="tel" required inputMode="numeric" pattern="\d{10}" maxLength={10} autoComplete="tel-national" value={cliente.telefono} onChange={(e) => setCliente({ ...cliente, telefono: e.target.value.replace(/\D/g, "") })} />
          </label>
          <label className="campo">{t.correo}
            <input type="email" autoComplete="email" value={cliente.correo} onChange={(e) => setCliente({ ...cliente, correo: e.target.value })} />
          </label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="boton" type="submit" disabled={ocupado}>{ocupado ? t.pagando : t.apartar}</button>
        </form>
      )}

      {paso === "apartado" && venta && (
        <div className="pila" style={{ display: "grid", gap: 12 }}>
          <div className="llenado prisa">
            <Img src="/img/reservas/cleto-espera.webp" alt="" aria-hidden="true" />
            <div>
              <strong>{t.apartado}</strong>
              <p>{t.expira} <strong>{Math.floor(segundos / 60)}:{String(segundos % 60).padStart(2, "0")}</strong></p>
            </div>
          </div>
          {(sinPasarela || demo) && <div className="pago-prueba" role="note">
            <div className="pago-prueba-tarjeta" aria-hidden="true"><span>VISA</span><b>4242 4242 4242 4242</b><small>{p.idioma === "es" ? "Tarjeta de prueba" : "Test card"}</small></div>
            <p><b>{p.idioma === "es" ? "Pago simulado" : "Simulated payment"}</b> {p.idioma === "es" ? "Este sitio está en modo práctica: aquí abriría Mercado Pago. No se cobra nada y el boleto es de prueba." : "This site is in practice mode: Mercado Pago would open here. Nothing is charged and the ticket is a test."}</p>
          </div>}
          {error && <p className="error" role="alert">{error}</p>}
          <button className="boton verde reserva-pagar" type="button" disabled={ocupado || segundos === 0} onClick={pagar}>{sinPasarela || demo ? (p.idioma === "es" ? "Simular pago aprobado" : "Simulate approved payment") : (p.idioma === "es" ? `Pagar ${formatoMXN(venta.totalCentavos, p.idioma)}` : `Pay ${formatoMXN(venta.totalCentavos, p.idioma)}`)}</button>
          <Resumen idioma={p.idioma} tour={p.nombreTour} fecha={fecha} hora={salida?.hora ?? ""} personas={p.porVuelo ? (p.idioma === "es" ? `${adultos} en el vuelo` : `${adultos} on the flight`) : t.desglose(adultos, ninos)} total={formatoMXN(venta.totalCentavos, p.idioma)} contacto={[cliente.nombre, cliente.telefono, cliente.correo].filter(Boolean).join(" · ")} folio={venta.folio} />
          <p className="tenue" style={{ fontSize: "0.85rem" }}>{p.idioma === "es" ? "Si algo no está bien, no pagues: deja que el apartado venza y vuelve a reservar." : "If something is wrong, don't pay: let the hold expire and book again."}</p>
        </div>
      )}
    </div>
  );
}

function Contador({ etiqueta, valor, min, max, cambiar }: { etiqueta: string; valor: number; min: number; max: number; cambiar: (n: number) => void }) {
  return (
    <div className="contador">
      <span style={{ fontWeight: 700 }}>{etiqueta}</span>
      <div className="ctl">
        <button type="button" aria-label={`Menos ${etiqueta.toLowerCase()}`} disabled={valor <= min} onClick={() => cambiar(valor - 1)}><Icono nombre="menos" tam={16} /></button>
        <output aria-live="polite">{valor}</output>
        <button type="button" aria-label={`Más ${etiqueta.toLowerCase()}`} disabled={valor >= max} onClick={() => cambiar(valor + 1)}><Icono nombre="mas" tam={16} /></button>
      </div>
    </div>
  );
}

// Resumen de la reserva antes de confirmar (todo a la vista, sin tener que recordar el paso anterior).
function Resumen({ idioma, tour, fecha, hora, personas, total, contacto, folio, editar }: { idioma: Idioma; tour: string; fecha: string; hora: string; personas: string; total: string; contacto?: string; folio?: string; editar?: () => void }) {
  const es = idioma === "es";
  const dia = new Date(`${fecha}T12:00:00Z`).toLocaleDateString(es ? "es-MX" : "en-US", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
  const filas: [string, string][] = [["Tour", tour], [es ? "Día" : "Day", dia], [es ? "Hora de salida" : "Departure", hora], [es ? "Personas" : "Guests", personas]];
  if (contacto) filas.push([es ? "Contacto" : "Contact", contacto]);
  if (folio) filas.push([es ? "Folio" : "Booking ref.", folio]);
  return (
    <section className="reserva-resumen" aria-label={es ? "Resumen de tu reserva" : "Booking summary"}>
      <header><strong>{es ? "Tu reserva" : "Your booking"}</strong>{editar && <button type="button" className="enlace-codigo" onClick={editar}>{es ? "Editar" : "Edit"}</button>}</header>
      <dl>{filas.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      <footer><span>Total<small>{es ? "Tienes 15 minutos para pagar; después se liberan los lugares." : "Online payment. Unpaid seats are released after 15 minutes."}</small></span><b>{total}</b></footer>
    </section>
  );
}
